import classNames from "classnames";
import queryString from "query-string";
import {useEffect, useState} from "react";
import {useLocation, useNavigate, useSearchParams} from "react-router-dom";
import {common as stringsCommon, loginPage as strings} from "strings";

import Button from "components/Button";
import TextInput from "components/TextInput";
import UnauthLayout from "components/UnauthLayout";

import {useGetAppointmentCampaigns} from "liberty-core/api/react-query/appointment";

import {useAlert} from "lib/alert";
import {signIn} from "lib/auth";
import {redirectToParamsPage} from "lib/redirect";
import {saveLocalStorage, KEYS, getLocalStorageOrDefault} from "lib/storage";
import {useCurrentPharmacy} from "lib/useCurrentPharmacy";

import styles from "./LoginPage.module.css";

type LocationState =
  | undefined
  | {
      recoveredEmail?: string;
    };

export default function LoginPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const locationState = location.state as LocationState;
  const recoveredEmail = locationState?.recoveredEmail;
  const alert = useAlert();
  const [pharmacy] = useCurrentPharmacy();
  const isDisabled = pharmacy?.rx365Enabled === false;
  const navigate = useNavigate();
  const {data: apptCampaigns, isLoading: isLoadingApptCampaigns} =
    useGetAppointmentCampaigns(pharmacy?.id ?? 0, {
      enabled: !!pharmacy && searchParams.get("pageName") === "APPOINTMENTS",
    });

  useEffect(() => {
    const parsed = queryString.parse(location.search);
    if (parsed.mismatched === "true") {
      alert({
        title: strings.redirected,
        message: strings.youHaveBeenRedirected,
      });
    }
  }, [alert, location.search]);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSignIn = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    signIn(username, password)
      .then(() => {
        saveLocalStorage(KEYS.email, username);
        setLoading(false);
        redirectToParamsPage(navigate, searchParams, null, apptCampaigns ?? []);
      })
      .catch((err) => {
        if (
          err.response &&
          err.response.status === 400 &&
          err.response.data &&
          err.response.data.message
        ) {
          alert({
            title: stringsCommon.whoops,
            message: err.response.data.message,
          });
        } else {
          alert({
            title: stringsCommon.whoops,
            message:
              err?.response?.data?.generic?.join("\n") ??
              stringsCommon.somethingWentWrong,
          });
        }
        setLoading(false);
      });
  };

  useEffect(() => {
    if (recoveredEmail) {
      setUsername(recoveredEmail);
    } else {
      const email = getLocalStorageOrDefault(KEYS.email, "");
      setUsername(email);
    }
  }, [recoveredEmail]);

  return (
    <UnauthLayout
      rightLink={
        isDisabled
          ? undefined
          : {
              label: stringsCommon.register,
              to: `/register?${searchParams.toString()}`,
            }
      }
    >
      <form className={styles.loginInfo} onSubmit={onSignIn}>
        <div className={classNames(styles.loginText, styles.loginInput)}>
          {stringsCommon.login}
        </div>
        {recoveredEmail ? (
          <div className={styles.emailRecovered}>{strings.emailRecovered}</div>
        ) : null}
        {isDisabled ? (
          <div className={styles.disabledText}>{strings.disabled}</div>
        ) : (
          <>
            <TextInput
              className={styles.itemPadding}
              label={stringsCommon.email}
              type="text"
              autoComplete="username"
              value={username}
              onChangeText={setUsername}
            />
            <TextInput
              className={styles.itemPadding}
              label={stringsCommon.password}
              type="password"
              autoComplete="current-password"
              value={password}
              onChangeText={setPassword}
            />
            <Button
              className={styles.itemPadding}
              type="submit"
              loading={loading}
              disabled={!username || !password}
            >
              {stringsCommon.login}
            </Button>
            <div className={styles.forgotContainer}>
              <div>{strings.forgot}</div>
              <Button
                className={styles.forgotButton}
                labelClassName={styles.forgotButtonLabel}
                type="button"
                variant="minimal"
                to={`/forgot-email?${searchParams.toString()}`}
              >
                {stringsCommon.email}
              </Button>
              <div>{strings.or}</div>
              <Button
                className={styles.forgotButton}
                labelClassName={styles.forgotButtonLabel}
                type="button"
                variant="minimal"
                to={`/forgot-password?${searchParams.toString()}`}
                loading={isLoadingApptCampaigns}
              >
                {stringsCommon.password}
              </Button>
              <div>{strings.questionMark}</div>
            </div>
          </>
        )}
      </form>
    </UnauthLayout>
  );
}
